// Menus around the fight: title → dojo board (belt ladder) → exam intro →
// fight → result. Owns the save; the Game only reports how a match ended.
// All player-facing text comes from i18n; screens re-render on a language
// switch.

import {
  BELTS,
  BLACK,
  EXAMS,
  MARK,
  marksFor,
  perfectsFor,
  starsFor,
  stripeChain,
  type Belt,
} from '@/content/exams';
import { ruleFor, type DailyRule } from '@/content/daily';
import { DAY_MS, addDays, dayNumber } from '@/core/dates';
import { countdown, recordTraining, streakState, weekStrip, type Training } from '@/meta/streak';
import {
  DOJOS,
  distanceKm,
  dojoById,
  randomDojo,
  trialLink,
  type Dojo,
  type Placement,
} from '@/content/dojos';
import { loadCity, roomThumb } from '@/render/stages';
import type { EnemyKind } from '@/sim/tuning';
import { drawPortrait } from '@/render/renderer';
import { loadBossLooks } from '@/render/rig';
import { track } from '@/meta/analytics';
import { challengeUrl, loadShareCard, type ShareCard, type ShareOutcome } from '@/ui/share';
import { formatChallenge, parseChallenge } from '@/meta/challenge';
import type { Game, MatchResult } from '@/app/game';
import { coachFor } from '@/app/coach';
import { roundsFor, opponentsIn, stageFor, type Mode } from '@/meta/modes';
import {
  loadConsent,
  loadSave,
  persist,
  recordDaily,
  recordExam,
  recordKumite,
  setConsent,
  todayISO,
  type Consent,
  type Save,
} from '@/meta/save';
import {
  LANG_NAMES,
  LANGS,
  applyDom,
  currentLang,
  fmt,
  isLang,
  onLangChange,
  setLang,
  t,
  type MessageKey,
} from '@/i18n';

export type ScreenName = 'title' | 'dojo' | 'intro' | 'playing' | 'result' | 'picker';

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

const LOCK =
  '<svg viewBox="0 0 16 16"><path d="M4 7V5a4 4 0 1 1 8 0v2h1v8H3V7h1zm2 0h4V5a2 2 0 1 0-4 0v2z"/></svg>';

/** What each screen focuses when it opens, so keyboard and screen-reader
 *  users land on the next step instead of on <body>. */
const FOCUS: Partial<Record<ScreenName, string>> = {
  title: 'start-btn',
  dojo: 'exam-btn',
  intro: 'intro-go',
  result: 'result-next',
  picker: 'picker-title',
};

/** Picker card thumbnails: CSS pixels. */
const THUMB_W = 96;
const THUMB_H = 64;

/** Between facts in a line: it may break after the dot, never before it. */
const SEP = '\u00a0· ';

const stars = (n: number): string => '★'.repeat(n) + '☆'.repeat(3 - n);

const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Result stars as separate glyphs, so the earned ones can pop in one by one. */
function fillStars(node: HTMLElement, n: number): void {
  node.replaceChildren(
    ...[...stars(n)].map((glyph, i) => {
      const span = document.createElement('span');
      span.textContent = glyph;
      span.className = i < n ? 'star earned' : 'star';
      span.style.animationDelay = `${0.25 + i * 0.22}s`;
      return span;
    }),
  );
}

/** Roll a number up to its value (formatted per locale). */
function countUp(node: HTMLElement, value: number, seconds = 0.9): void {
  if (reducedMotion() || value <= 0) {
    node.textContent = fmt(value);
    return;
  }
  const start = performance.now();
  const step = (now: number): void => {
    const k = Math.min(1, (now - start) / 1000 / seconds);
    node.textContent = fmt(Math.round(value * (1 - (1 - k) ** 3)));
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
const beltName = (b: Belt): string => t(`belt.${b.id}` as MessageKey);
const ruleName = (r: DailyRule): string => t(`rule.${r.id}.name`);
const beltColor = (b: Belt): string => t(`color.${b.id}` as MessageKey);

function paint(node: HTMLElement, belt: { color: string; edge: string }): void {
  node.style.setProperty('--belt', belt.color);
  node.style.setProperty('--edge', belt.edge);
}

export class App {
  save: Save = loadSave();
  /** Whether progress may be written to this device (asked after the first belt). */
  consent: Consent = loadConsent();
  screen: ScreenName = 'title';
  private pending: Mode = { kind: 'exam', exam: 0 };
  private lastResult: MatchResult | null = null;
  /** What the result card's primary button does next. */
  private next: (() => void) | null = null;
  /** Screen to return to when the dojo picker closes. */
  private pickerReturn: ScreenName = 'dojo';
  private boardCtaTracked = false;
  /** Distance to each dojo (km), once the player asked for the nearest one.
   *  Kept for this visit only; the position itself is never stored. */
  private near: Map<string, number> | null = null;
  private thumbs = new Map<string, Promise<HTMLCanvasElement>>();
  /** A history entry of our own is on the stack, so the back button stays in the game. */
  private armed = false;
  private skipPop = false;
  private toastTimer = 0;
  private askTimer = 0;
  /** The day the current daily exam run started on: it counts for that day. */
  private startedOn = '';

  constructor(private readonly game: Game) {
    game.onEnd = (r) => this.onResult(r);
    // Once the player has used what the coach taught, it's not shown again
    game.onCoached = (bits) => {
      this.save.coach |= bits;
      this.keep();
    };
    game.onPauseChange = (p) => {
      document.body.classList.toggle('paused', p);
      if (p) el('resume-btn').focus();
      else this.blur();
    };
    // Every menu button answers with a soft wood-block click
    document.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.closest('button, a.btn')) game.sfx.click();
    });
    el('start-btn').addEventListener('click', () => {
      game.sfx.unlock();
      this.showDojo();
    });
    el('exam-btn').addEventListener('click', () => this.openIntro(this.primaryMode()));
    el('daily-btn').addEventListener('click', () =>
      this.openIntro({ kind: 'daily', date: todayISO() }),
    );
    el('kumite-btn').addEventListener('click', () => this.openIntro({ kind: 'kumite' }));
    el('intro-go').addEventListener('click', () => this.fight(this.pending));
    el('intro-back').addEventListener('click', () => this.showDojo());
    el('result-next').addEventListener('click', () => this.next?.());
    el('result-dojo').addEventListener('click', () => this.showDojo());
    el('board-menu').addEventListener('click', () => this.show('title'));
    for (const id of ['cta-change', 'board-cta-change']) {
      el(id).addEventListener('click', () => this.openPicker());
    }
    el('picker-later').addEventListener('click', () => this.closePicker());
    el('picker-near').addEventListener('click', () => this.findNearest());
    // Every trial link carries its placement and dojo in the UTM tags: one
    // funnel step for all of them (Matomo's link tracking records the outlink)
    document.addEventListener('click', (e) => {
      const a = e.target instanceof Element ? e.target.closest('a') : null;
      if (!a?.href.includes('utm_source=kiai-game')) return;
      const q = new URL(a.href).searchParams;
      const term = q.get('utm_term') ?? '';
      const kids = term.includes('kids') ? ':kids' : '';
      const auto = term.includes('preselected') ? ':auto' : '';
      track('Promo', 'CtaClicked', `${q.get('utm_medium')}:${q.get('utm_campaign')}${kids}${auto}`);
    });
    el('result-share').addEventListener('click', () => void this.share());
    el('pause-btn').addEventListener('click', () => game.setPaused(true));
    el('resume-btn').addEventListener('click', () => game.setPaused(false));
    el('leave-btn').addEventListener('click', () => {
      game.abort();
      document.body.classList.remove('paused');
      this.showDojo();
    });
    el('restart-btn').addEventListener('click', () => {
      const mode = game.mode;
      game.abort();
      document.body.classList.remove('paused');
      track('Game', 'Restarted', mode.kind);
      this.fight(mode);
    });
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'Enter' && e.code !== 'Space') return;
      // The save dialog has the focus; don't skip past it
      if (!el('save-ask').hidden) return;
      // A focused button or picker already handles its own keys
      if (
        e.target instanceof HTMLButtonElement ||
        e.target instanceof HTMLSelectElement ||
        e.target instanceof HTMLAnchorElement
      )
        return;
      const primary: Partial<Record<ScreenName, string>> = {
        title: 'start-btn',
        dojo: 'exam-btn',
        intro: 'intro-go',
        result: 'result-next',
      };
      const id = primary[this.screen];
      if (!id) return;
      e.preventDefault();
      el(id).click();
    });
    window.addEventListener('keydown', (e) => {
      if (!el('save-ask').hidden && e.key === 'Tab') this.trapConsentFocus(e);
      // Escape is the in-game back button; during a match the game pauses itself
      if (e.key === 'Escape' && this.screen !== 'playing') {
        e.preventDefault();
        this.back();
      }
    });
    window.addEventListener('popstate', () => {
      this.armed = false;
      if (this.skipPop) {
        this.skipPop = false;
        return;
      }
      if (this.back()) this.arm();
    });

    const picker = el<HTMLSelectElement>('lang-select');
    picker.replaceChildren(
      ...LANGS.map((l) => {
        const o = document.createElement('option');
        o.value = l;
        o.textContent = LANG_NAMES[l];
        return o;
      }),
    );
    picker.value = currentLang();
    picker.addEventListener('change', () => {
      if (isLang(picker.value)) void setLang(picker.value);
    });
    onLangChange(() => this.rerender());
    for (const b of document.querySelectorAll<HTMLButtonElement>('.sound-btn')) {
      b.addEventListener('click', () => {
        game.sfx.unlock();
        game.sfx.setMuted(!game.sfx.isMuted);
        this.paintSound();
      });
    }
    this.paintSound();
    el('save-yes').addEventListener('click', () => this.answerConsent(true));
    el('save-no').addEventListener('click', () => this.answerConsent(false));
    el('progress-btn').addEventListener('click', () => {
      const on = this.consent !== 'yes';
      this.answerConsent(on);
      this.toast(on ? t('consent.saved') : t('consent.deleted'));
    });
    this.paintProgress();

    // A screen's buttons stick to the bottom only while it scrolls (.actions)
    const fits = new ResizeObserver((entries) => {
      for (const e of entries) {
        const screen = e.target.closest<HTMLElement>('.screen');
        screen?.classList.toggle('scrolls', screen.scrollHeight > screen.clientHeight + 1);
      }
    });
    for (const screen of document.querySelectorAll('.screen')) {
      fits.observe(screen);
      for (const child of screen.children) fits.observe(child);
    }

    this.applyBelt();
    this.game.setDojo(this.dojo().dojo);
    this.show('title', false);

    this.openChallenge();

    // Keep the countdown to the next daily exam current, and roll the board
    // over to the new day's exam at midnight
    let day = todayISO();
    window.setInterval(() => {
      const today = todayISO();
      if (this.screen === 'dojo') {
        if (today !== day) this.showDojo(false);
        else this.paintWeek();
      } else if (this.screen === 'result' && this.lastResult?.mode.kind === 'daily') {
        this.paintDailyNext(this.lastResult.mode.date);
      }
      day = today;
    }, 30_000);
  }

  show(screen: ScreenName, focus = true): void {
    this.screen = screen;
    document.body.dataset.screen = screen;
    if (screen === 'title') {
      // Home: drop our history entry, so back from here leaves the game
      if (this.armed) {
        this.armed = false;
        this.skipPop = true;
        history.back();
      }
    } else this.arm();
    if (!focus) return;
    const id = FOCUS[screen];
    if (id) el(id).focus({ preventScroll: true });
    else this.blur();
  }

  /** The player's pick, or until then a random dojo, drawn once into the
   *  save: it lasts across visits only for players who keep their progress. */
  private dojo(): { dojo: Dojo; picked: boolean } {
    const picked = dojoById(this.save.dojo);
    if (picked) return { dojo: picked, picked: true };
    let drawn = dojoById(this.save.drawn);
    if (!drawn) {
      drawn = randomDojo();
      this.save.drawn = drawn.id;
      this.keep();
    }
    return { dojo: drawn, picked: false };
  }

  /** CtaShown name (CtaClicked reads the same from the link's UTM tags): where,
   *  which dojo, and `:auto` for the random one. */
  private ctaName(placement: Placement): string {
    const { dojo, picked } = this.dojo();
    return `${placement}:${dojo.id}${picked ? '' : ':auto'}`;
  }

  private arm(): void {
    if (this.armed) return;
    history.pushState({ kiai: true }, '');
    this.armed = true;
  }

  private blur(): void {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }

  /**
   * One step back (Escape, the browser's back button). Returns whether the
   * player is still inside the game afterwards.
   */
  private back(): boolean {
    if (!el('save-ask').hidden) {
      this.answerConsent(false);
      return true;
    }
    switch (this.screen) {
      case 'playing':
        this.game.setPaused(true);
        return true;
      case 'picker':
        this.closePicker();
        return true;
      case 'intro':
      case 'result':
        this.showDojo();
        return true;
      case 'dojo':
        this.show('title');
        return false;
      default:
        return false;
    }
  }

  /** Keep Tab inside the save question while it's up. */
  private trapConsentFocus(e: KeyboardEvent): void {
    const first = el('save-yes');
    const last = el('save-no');
    const inside = el('save-ask').contains(document.activeElement);
    if (!inside || (e.shiftKey && document.activeElement === first)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private paintSound(): void {
    const label = this.game.sfx.isMuted ? t('sound.off') : t('sound.on');
    for (const b of document.querySelectorAll<HTMLButtonElement>('.sound-btn')) {
      b.textContent = label;
      b.setAttribute('aria-pressed', String(!this.game.sfx.isMuted));
    }
  }

  /** The title's list of partner dojos, each with its free trial for adults
   *  and for kids (tagged with the belt the player has, as every trial link). */
  private paintDojoList(): void {
    const beltId = (BELTS[this.save.belt] ?? BELTS[0]!).id;
    const trial = (d: Dojo, kids: boolean): HTMLAnchorElement => {
      const a = document.createElement('a');
      a.className = 'dojo-trial';
      a.target = '_blank';
      a.rel = 'noopener';
      a.href = trialLink(d, 'title', beltId, true, kids);
      a.textContent = t(kids ? 'title.dojoKids' : 'title.dojoAdults');
      a.setAttribute(
        'aria-label',
        t(kids ? 'title.dojoKidsAria' : 'title.dojoAdultsAria', { dojo: d.name }),
      );
      return a;
    };
    el('title-dojos').replaceChildren(
      ...DOJOS.map((d) => {
        const li = document.createElement('li');
        const where = document.createElement('span');
        where.className = 'dojo-where';
        const name = document.createElement('b');
        name.textContent = d.name;
        const area = document.createElement('small');
        area.textContent = d.area;
        where.append(name, area);
        li.append(where, trial(d, false), trial(d, true));
        return li;
      }),
    );
  }

  private paintProgress(): void {
    const b = el('progress-btn');
    b.textContent = this.consent === 'yes' ? t('consent.chipOn') : t('consent.chipOff');
    b.setAttribute('aria-pressed', String(this.consent === 'yes'));
  }

  /** Keep progress on this device only if the player agreed. */
  private keep(): void {
    if (this.consent === 'yes') persist(this.save);
  }

  private askToSave(what: 'belt' | 'streak'): void {
    if (this.screen !== 'result' || this.consent !== null) return;
    const streak = what === 'streak';
    el('save-ask-title').dataset.i18n = streak ? 'consent.askStreak' : 'consent.ask';
    el('save-ask-detail').dataset.i18n = streak ? 'consent.detailStreak' : 'consent.detail';
    applyDom(el('save-ask'));
    el('save-ask').hidden = false;
    el('save-yes').focus();
  }

  private answerConsent(yes: boolean): void {
    this.consent = setConsent(yes, this.save);
    const wasAsking = !el('save-ask').hidden;
    el('save-ask').hidden = true;
    if (wasAsking && this.screen === 'result') el('result-next').focus({ preventScroll: true });
    if (yes && this.screen === 'result') this.toast(t('consent.saved'));
    this.paintProgress();
    track('Game', 'SaveConsent', yes ? 'yes' : 'no');
  }

  /** Re-fill the dynamic text of whatever screen is up after a language switch. */
  private rerender(): void {
    this.paintSound();
    this.paintProgress();
    this.paintDojoList();
    el<HTMLSelectElement>('lang-select').value = currentLang();
    if (this.screen === 'dojo') this.showDojo();
    else if (this.screen === 'picker') this.renderPicker();
    else if (this.screen === 'intro') this.openIntro(this.pending);
    else if (this.screen === 'result' && this.lastResult) this.fillResult(this.lastResult, false);
  }

  /** A friend's challenge link (?c=): straight to its intro. The token (and
   *  the preview's language) leave the address bar, so a reload or bookmark
   *  doesn't replay it. */
  private openChallenge(): void {
    const token = new URLSearchParams(location.search).get('c');
    if (token === null) return;
    const url = new URL(location.href);
    url.searchParams.delete('c');
    url.searchParams.delete('hl');
    history.replaceState(history.state, '', url);
    const mode = parseChallenge(token, todayISO());
    if (!mode) {
      track('Challenge', 'Invalid');
      return;
    }
    const name =
      mode.kind === 'daily'
        ? mode.date === todayISO()
          ? 'daily'
          : 'daily:past'
        : mode.kind === 'exam'
          ? `exam:${BELTS[EXAMS[mode.exam]!.to]!.id}`
          : 'kumite';
    track('Challenge', 'Opened', `${name}${mode.rival?.stale ? ':stale' : ''}`);
    this.openIntro(mode);
  }

  /** Whether a run of `mode` counts toward progress: a friend's challenge can
   *  be a locked exam, Kumite before black belt or another day's exam. */
  private counts(mode: Mode): boolean {
    switch (mode.kind) {
      case 'exam':
        return mode.exam <= this.save.belt;
      case 'kumite':
        return this.save.belt >= BLACK;
      case 'daily':
        return mode.date === todayISO();
    }
  }

  /** Never fought before: a challenge link may be their first visit. */
  private newcomer(): boolean {
    return this.save.exams === 0 && this.save.train.total === 0;
  }

  /** "A friend scored 12,400 in today's daily exam — same opponents. Beat it." */
  private challengeText(mode: Mode, score: number): string {
    switch (mode.kind) {
      case 'daily': {
        if (mode.date === todayISO()) return t('challenge.dailyToday', { score });
        const day = new Intl.DateTimeFormat(currentLang(), {
          day: 'numeric',
          month: 'long',
          timeZone: 'UTC',
        });
        return t('challenge.dailyOn', { score, date: day.format(dayNumber(mode.date) * DAY_MS) });
      }
      case 'exam':
        return t('challenge.exam', { score, belt: beltName(BELTS[EXAMS[mode.exam]!.to]!) });
      case 'kumite':
        return t('challenge.kumite', { score });
    }
  }

  /** The board's main button: the next exam, or Kumite once every belt is earned. */
  private primaryMode(): Mode {
    if (this.save.belt >= BLACK) return { kind: 'kumite' };
    return { kind: 'exam', exam: this.save.belt };
  }

  private applyBelt(): void {
    const belt = BELTS[this.save.belt] ?? BELTS[0]!;
    this.game.setBelt(belt.color, belt.edge);
    this.paintDojoList();
  }

  showDojo(focus = true): void {
    const s = this.save;
    const belt = BELTS[s.belt]!;
    el('dojo-belt').textContent = beltName(belt);
    paint(el('dojo-swatch'), belt);

    const ladder = el('belt-ladder');
    // What the hanko and the gold stripe mean, once there's a passed exam to retake for them
    el('ladder-legend').hidden = !s.stars.some((n) => n > 0);
    // The belts still locked fold into one row (a single one stays a rung)
    const locked = EXAMS.length - 1 - s.belt;
    const shown = locked >= 2 ? EXAMS.slice(0, s.belt + 1) : EXAMS;
    const rest = document.createElement('li');
    if (locked >= 2) {
      const row = document.createElement('div');
      row.className = 'locked-rest';
      const chips = document.createElement('span');
      chips.className = 'chips';
      chips.setAttribute('aria-hidden', 'true');
      for (const exam of EXAMS.slice(s.belt + 1)) {
        const chip = document.createElement('span');
        chip.className = 'chip';
        paint(chip, BELTS[exam.to]!);
        chips.append(chip);
      }
      const text = document.createElement('span');
      text.className = 'rest-name';
      text.textContent = t('dojo.moreBelts', { count: locked });
      const lock = document.createElement('span');
      lock.className = 'lock';
      lock.innerHTML = LOCK;
      lock.setAttribute('aria-hidden', 'true');
      row.append(chips, text, lock);
      rest.append(row);
    }
    ladder.replaceChildren(
      ...shown.map((exam, i) => {
        const to = BELTS[exam.to]!;
        const passed = (s.stars[i] ?? 0) > 0;
        const open = i <= s.belt;
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `rung${passed ? ' passed' : ''}${i === s.belt ? ' current' : ''}`;
        btn.disabled = !open;
        paint(btn, to);
        const chip = document.createElement('span');
        chip.className = 'chip';
        const name = document.createElement('span');
        name.className = 'rung-name';
        name.textContent = beltColor(to);
        const state = document.createElement('span');
        state.className = 'rung-stars';
        // Mastery: a gold stripe on the belt, a hanko by the stars
        const marks = s.marks[i] ?? 0;
        if (marks & MARK.stripe) {
          chip.classList.add('striped');
          chip.setAttribute('aria-label', t('dojo.stripe'));
        }
        if (passed) {
          state.textContent = stars(s.stars[i] ?? 0);
          if (marks & MARK.stamp) {
            const seal = document.createElement('span');
            seal.className = 'seal';
            seal.setAttribute('role', 'img');
            seal.setAttribute('aria-label', t('dojo.stamp'));
            state.prepend(seal);
          }
        } else if (open) state.textContent = t('dojo.exam');
        else {
          state.innerHTML = LOCK;
          const label = document.createElement('span');
          label.className = 'sr';
          label.textContent = t('dojo.locked');
          state.append(label);
        }
        btn.append(chip, name, state);
        btn.addEventListener('click', () => this.openIntro({ kind: 'exam', exam: i }));
        li.append(btn);
        return li;
      }),
      ...(locked >= 2 ? [rest] : []),
    );

    // Every belt earned: Kumite takes the main button (retakes stay on the ladder)
    const done = s.belt >= BLACK;
    const examBtn = el('exam-btn');
    if (done) {
      examBtn.textContent = t('result.kumiteNext');
      if (s.kumiteBest > 0) {
        const best = document.createElement('small');
        best.textContent = t('dojo.best', { score: s.kumiteBest });
        examBtn.append(best);
      }
    } else examBtn.textContent = t('dojo.take', { belt: beltName(BELTS[s.belt + 1]!) });
    const today = todayISO();
    el('daily-rule').textContent = ruleName(ruleFor(today));
    el('daily-best').textContent =
      s.daily.date === today && s.daily.best > 0
        ? t('dojo.best', { score: s.daily.best })
        : t('dojo.dailyNew');
    el<HTMLButtonElement>('kumite-btn').disabled = !done;
    el('kumite-btn').hidden = done;
    el('kumite-note').textContent = done
      ? s.kumiteBest > 0
        ? t('dojo.best', { score: s.kumiteBest })
        : t('dojo.kumiteEndless')
      : t('dojo.kumiteLocked');

    const { dojo, picked } = this.dojo();
    el('board-cta-sub').textContent = t('cta.boardSub', { dojo: dojo.name });
    el<HTMLAnchorElement>('board-cta-link').href = trialLink(dojo, 'dojo-board', belt.id, picked);
    el<HTMLAnchorElement>('board-cta-kids').href = trialLink(
      dojo,
      'dojo-board',
      belt.id,
      picked,
      true,
    );
    if (!this.boardCtaTracked) {
      this.boardCtaTracked = true;
      track('Promo', 'CtaShown', this.ctaName('dojo-board'));
    }
    this.paintWeek();
    this.show('dojo', focus);
  }

  /** "Next exam in 5 h 12 min" (the daily exam turns over at local midnight). */
  private nextIn(): string {
    const { h, m } = countdown();
    return h > 0 ? t('daily.nextIn', { h, m }) : t('daily.nextInMin', { m });
  }

  /** The board's training streak: count, the last seven days, what's next. */
  private paintWeek(): void {
    const s = this.save;
    const box = el('week');
    box.hidden = s.exams === 0 && s.train.total === 0;
    if (box.hidden) return;
    const today = todayISO();
    const { streak, state } = streakState(s, today);
    el('streak-count').textContent =
      state === 'none' ? t('streak.title') : t('streak.count', { count: streak });
    const letter = new Intl.DateTimeFormat(currentLang(), { weekday: 'narrow', timeZone: 'UTC' });
    const strip = weekStrip(s, today);
    el('week-days').replaceChildren(
      ...strip.map(({ day, done }, i) => {
        const li = document.createElement('li');
        li.textContent = letter.format(new Date(day * DAY_MS));
        if (done) li.classList.add('done');
        if (i === strip.length - 1) li.classList.add('today');
        return li;
      }),
    );
    el('week-sr').textContent = t('streak.week', { count: strip.filter((d) => d.done).length });
    el('week-note').textContent =
      this.consent === 'no'
        ? t('streak.unsaved')
        : state === 'done'
          ? this.nextIn()
          : state === 'open'
            ? t('streak.open')
            : state === 'risk'
              ? t('streak.atRisk')
              : t('streak.start');
    box.classList.toggle('risk', state === 'risk' && this.consent !== 'no');
  }

  /** Under a daily result: tomorrow's rule and when it opens. */
  private paintDailyNext(date: string): void {
    const rule = ruleName(ruleFor(addDays(date, 1)));
    const { h, m } = countdown();
    el('result-daily').textContent =
      h > 0 ? t('daily.nextRule', { rule, h, m }) : t('daily.nextRuleMin', { rule, m });
  }

  /** The streak after a daily exam, stamped with a hanko on a milestone. */
  private paintResultStreak(training: Training | undefined): void {
    const line = el('result-streak');
    const { streak } = streakState(this.save, todayISO());
    line.hidden = streak === 0;
    const milestone = training?.milestone ?? null;
    const text = document.createElement('span');
    text.textContent = milestone
      ? t('streak.milestone', { count: milestone })
      : t('streak.count', { count: streak });
    if (milestone) {
      const hanko = document.createElement('span');
      hanko.className = 'hanko';
      hanko.setAttribute('aria-hidden', 'true');
      hanko.textContent = String(milestone);
      line.replaceChildren(hanko, text);
    } else line.replaceChildren(text);
  }

  /** Funnel for coming back: the streak, the days since the last one, milestones. */
  private trackTraining(tr: Training): void {
    track('Daily', 'Streak', this.consent === 'yes' ? 'saved' : 'session', tr.streak);
    if (tr.gap !== null) track('Daily', 'Return', String(tr.gap), tr.streak);
    if (tr.milestone) track('Daily', 'Milestone', String(tr.milestone));
  }

  // ---- Dojo picker ----

  private openPicker(): void {
    if (this.screen !== 'picker') this.pickerReturn = this.screen;
    track('Promo', 'PickerOpened', this.pickerReturn);
    // The room behind the picker is the chosen dojo's, and changes with the pick
    this.game.showStage('dojo');
    this.renderPicker();
    this.show('picker');
  }

  private renderPicker(): void {
    const chosen = this.save.dojo;
    const beltId = BELTS[this.save.belt]!.id;
    const near = this.near;
    const order = near
      ? [...DOJOS].sort((a, b) => (near.get(a.id) ?? 0) - (near.get(b.id) ?? 0))
      : DOJOS;
    el('dojo-list').replaceChildren(
      ...order.map((d) => {
        const li = document.createElement('li');
        li.className = `dojo-card${d.id === chosen ? ' chosen' : ''}`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dojo-pick';
        btn.setAttribute('aria-pressed', String(d.id === chosen));
        const room = document.createElement('span');
        room.className = 'room';
        this.thumb(d).then(
          (c) => room.replaceChildren(c),
          () => undefined,
        );
        const info = document.createElement('span');
        info.className = 'info';
        const area = document.createElement('span');
        area.className = 'area';
        const km = near?.get(d.id);
        area.textContent = [
          d.area,
          km !== undefined
            ? t('picker.km', { km: km < 10 ? Math.round(km * 10) / 10 : Math.round(km) })
            : '',
          d.id === chosen ? t('picker.chosen') : '',
        ]
          .filter(Boolean)
          .join(' · ');
        const name = document.createElement('b');
        name.textContent = d.name;
        const addr = document.createElement('small');
        addr.textContent = d.address;
        info.append(area, name, addr);
        btn.append(room, info);
        btn.addEventListener('click', () => this.pickDojo(d));
        li.append(btn);
        if (d.id === chosen) {
          const link = document.createElement('a');
          link.className = 'btn gold trial';
          link.target = '_blank';
          link.rel = 'noopener';
          link.href = trialLink(d, 'picker', beltId);
          link.textContent = t('cta.adultsBtn');
          const kids = document.createElement('a');
          kids.className = 'btn ghost trial';
          kids.target = '_blank';
          kids.rel = 'noopener';
          kids.href = trialLink(d, 'picker', beltId, true, true);
          kids.textContent = t('cta.kidsBtn');
          li.append(link, kids);
        }
        return li;
      }),
    );
    el('picker-later').textContent = chosen ? t('picker.done') : t('picker.later');
  }

  /** The dojo's room, painted once per visit. */
  private thumb(d: Dojo): Promise<HTMLCanvasElement> {
    let p = this.thumbs.get(d.id);
    if (!p) {
      p = roomThumb(d.style, d.name, THUMB_W, THUMB_H);
      this.thumbs.set(d.id, p);
    }
    // A canvas can only sit in one card: hand out a copy each time
    return p.then((src) => {
      const c = document.createElement('canvas');
      c.width = src.width;
      c.height = src.height;
      c.getContext('2d')?.drawImage(src, 0, 0);
      return c;
    });
  }

  /** Sort the dojos by distance. Asks for the position only on this tap;
   *  the distance is worked out here and nothing leaves the device. */
  private findNearest(): void {
    const btn = el<HTMLButtonElement>('picker-near');
    const fail = (): void => {
      btn.disabled = false;
      this.toast(t('picker.nearFail'));
    };
    if (!('geolocation' in navigator)) return fail();
    btn.disabled = true;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        btn.disabled = false;
        const { latitude: lat, longitude: lon } = coords;
        this.near = new Map(DOJOS.map((d) => [d.id, distanceKm(lat, lon, d.lat, d.lon)]));
        track('Promo', 'NearestUsed');
        if (this.screen === 'picker') this.renderPicker();
      },
      fail,
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 },
    );
  }

  private pickDojo(d: Dojo): void {
    this.save.dojo = d.id;
    this.keep();
    this.game.setDojo(d);
    track('Promo', 'DojoPicked', d.id);
    // Stay: the card now offers its trial lesson, the room behind has changed
    this.renderPicker();
    el('dojo-list')
      .querySelector<HTMLElement>('.chosen .dojo-pick')
      ?.focus({ preventScroll: true });
  }

  private closePicker(): void {
    const r = this.lastResult;
    if (this.pickerReturn === 'result' && r) {
      // Only the promo changed; the belt and stars shouldn't be tied on again
      this.fillCta(r, r.mode.kind === 'exam' ? BELTS[EXAMS[r.mode.exam]!.to]! : null, false);
      document.body.classList.add('settled');
      this.show('result');
    } else this.showDojo();
  }

  /**
   * Fill the trial-lesson call to action on the result card: the paper card
   * after a pass or any Kumite run (which always ends in a loss), the
   * one-line nudge after a failed exam.
   */
  private fillCta(r: MatchResult, to: Belt | null, record: boolean): void {
    const { dojo, picked } = this.dojo();
    const beltId = (to ?? BELTS[this.save.belt]!).id;
    const kind = r.mode.kind;
    const cta = el('result-cta');
    const failCta = el('fail-cta');
    const card = r.passed || kind === 'kumite';
    cta.hidden = !card;
    failCta.hidden = card;
    let placement: Placement;
    if (card) {
      placement = kind === 'exam' ? 'promotion' : kind;
      el('cta-title').textContent =
        kind === 'kumite'
          ? t('cta.kumiteTitle')
          : to
            ? t('cta.promoTitle', { belt: beltName(to) })
            : t('cta.boardTitle');
      el('cta-sub').textContent = t('cta.promoSub', { dojo: dojo.name });
      const link = el<HTMLAnchorElement>('cta-link');
      link.href = trialLink(dojo, placement, beltId, picked);
      // Named on the button: short phones hide the line above it
      const where = document.createElement('small');
      where.textContent = dojo.name;
      link.replaceChildren(t('cta.boardBtn'), where);
      el<HTMLAnchorElement>('cta-kids').href = trialLink(dojo, placement, beltId, picked, true);
    } else {
      placement = 'fail';
      el<HTMLAnchorElement>('fail-cta-link').href = trialLink(dojo, 'fail', beltId, picked);
      el<HTMLAnchorElement>('fail-cta-kids').href = trialLink(dojo, 'fail', beltId, picked, true);
    }
    if (record) track('Promo', 'CtaShown', this.ctaName(placement));
  }

  // ---- Share ----

  private async share(): Promise<void> {
    const r = this.lastResult;
    if (!r) return;
    const score = fmt(r.score);
    // Every share is a challenge: the link replays these opponents
    const url = challengeUrl(formatChallenge(r.mode, r.seed, r.score), r.mode.kind);
    const back = !!r.mode.rival;
    let card: ShareCard;
    let text: string;
    if (r.mode.kind === 'exam' && r.passed && !r.practice) {
      const to = BELTS[EXAMS[r.mode.exam]!.to]!;
      card = {
        headline: t('share.headline', { belt: beltName(to) }),
        belt: to,
        stars: starsFor(r.perfects, r.hits),
        score,
      };
      text = back
        ? t('share.textBack', { score: r.score })
        : t('share.text', { belt: beltName(to), score: r.score });
    } else if (r.mode.kind === 'daily') {
      const rule = ruleName(ruleFor(r.mode.date));
      card = {
        tag: t('share.tagDaily', { rule }),
        headline: t('share.headlineScore', { score: r.score }),
        belt: null,
        stars: 0,
        score,
      };
      text = back
        ? t('share.textBack', { score: r.score })
        : t('share.textDaily', { score: r.score, rule });
    } else {
      card = {
        headline: t('share.headlineScore', { score: r.score }),
        belt: null,
        stars: 0,
        score,
      };
      text = back
        ? t('share.textBack', { score: r.score })
        : t('share.textScore', { score: r.score });
    }
    let outcome: ShareOutcome;
    try {
      outcome = await (await loadShareCard()).shareCard(card, text, url);
    } catch {
      // The card couldn't load (offline before it was ever cached): the link alone
      outcome = await navigator.clipboard.writeText(`${text} ${url}`).then(
        () => 'copied' as const,
        () => 'failed' as const,
      );
    }
    track('Share', outcome, `${r.mode.kind}${back ? ':back' : ''}`);
    if (outcome === 'downloaded') this.toast(t('share.saved'));
    else if (outcome === 'saved') this.toast(t('share.savedOnly'));
    else if (outcome === 'copied') this.toast(t('share.copied'));
  }

  private toast(text: string): void {
    const node = el('toast');
    node.textContent = text;
    node.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (node.hidden = true), 2600);
  }

  private openIntro(mode: Mode): void {
    this.pending = mode;
    const rounds = roundsFor(mode);
    const count = t('intro.rounds', { count: rounds.length });
    const opponents = t('intro.opponents', { count: opponentsIn(rounds) });
    const tip = el('intro-tip');
    tip.hidden = true;
    const goals = el('intro-goals');
    goals.hidden = true;
    // The opponent the card is about, if any (drawn once the card is laid out)
    let portrait: EnemyKind | undefined;
    if (mode.kind === 'exam') {
      const exam = EXAMS[mode.exam]!;
      const to = BELTS[exam.to]!;
      el('intro-title').textContent = t('intro.examTitle', { belt: beltName(to) });
      el('intro-sub').textContent = [count, opponents, t('intro.hitsOut')].join(SEP);
      paint(el('intro-swatch'), to);
      // Once passed, the marks still to earn (and the ones already earned)
      if ((this.save.stars[mode.exam] ?? 0) > 0 && !mode.rival) {
        const marks = this.save.marks[mode.exam] ?? 0;
        goals.replaceChildren(
          ...[
            [MARK.stamp, t('goal.stamp')],
            [MARK.stripe, t('goal.stripe', { count: stripeChain(mode.exam) })],
          ].map(([bit, text]) => {
            const span = document.createElement('span');
            span.className = (marks & (bit as number)) !== 0 ? 'goal done' : 'goal';
            span.textContent = text as string;
            return span;
          }),
        );
        goals.hidden = false;
      }
      if (exam.introduces) {
        const kind = exam.introduces;
        el('intro-tip-name').textContent =
          kind === 'basic'
            ? t('intro.firstOpponents')
            : t('intro.newOpponent', { name: t(`opp.${kind}.name`) });
        el('intro-tip-text').textContent = t(`opp.${kind}.tip`);
        tip.hidden = false;
        portrait = kind;
      }
    } else if (mode.kind === 'daily') {
      const rule = ruleFor(mode.date);
      el('intro-title').textContent = t('intro.dailyTitle');
      el('intro-sub').textContent = [t('intro.dailySub'), count, opponents].join(SEP);
      paint(el('intro-swatch'), { color: '#ffd166', edge: '#c9a21c' });
      el('intro-tip-name').textContent = t('intro.todayRule', { rule: ruleName(rule) });
      el('intro-tip-text').textContent = t(`rule.${rule.id}.desc`);
      tip.hidden = false;
      portrait = rule.features;
    } else {
      el('intro-title').textContent = t('intro.kumiteTitle');
      el('intro-sub').textContent = t('intro.kumiteSub');
      paint(el('intro-swatch'), BELTS[BLACK]!);
    }
    // A friend's challenge: their score to beat, and what to know before
    const note = el('intro-note');
    note.hidden = !mode.rival;
    if (mode.rival) {
      el('intro-title').textContent = t('challenge.title');
      el('intro-sub').textContent = this.challengeText(mode, mode.rival.score);
      note.textContent = [
        mode.rival.stale ? t('challenge.stale') : '',
        this.counts(mode) ? '' : t('challenge.practice'),
        this.newcomer() ? t('title.howto1') : '',
      ]
        .filter(Boolean)
        .join(' ');
      note.hidden = !note.textContent;
    }
    // A stage's last exam: the boss who ends it
    const boss =
      mode.kind === 'exam'
        ? EXAMS[mode.exam]!.boss
        : mode.kind === 'daily'
          ? ruleFor(mode.date).boss
          : undefined;
    el('intro-boss').hidden = !boss;
    if (boss) {
      el('intro-boss-name').textContent = t('intro.finalOpponent', {
        name: t(`boss.${boss}.name`),
      });
      el('intro-boss-text').textContent = t(`boss.${boss}.tip`);
    }
    const art = el<HTMLCanvasElement>('intro-tip-art');
    art.hidden = !portrait;
    // Out in Berlin: the skyline loads while the intro is read
    if (stageFor(mode) !== 'dojo') void loadCity();
    this.show('intro');
    // Drawn once the card is laid out, at its CSS size
    if (portrait) drawPortrait(art, portrait);
    // The boss's outfit loads on demand: his portrait as soon as it's in
    if (boss) {
      const canvas = el<HTMLCanvasElement>('intro-boss-art');
      void loadBossLooks().then(() => drawPortrait(canvas, boss));
    }
  }

  private fight(mode: Mode): void {
    // An intro (or a result) left open past midnight: today's exam instead
    const today = todayISO();
    if (mode.kind === 'daily' && !mode.rival && mode.date !== today) {
      this.openIntro({ kind: 'daily', date: today });
      return;
    }
    this.startedOn = today;
    if (mode.rival) track('Challenge', 'Started', mode.kind);
    const name =
      mode.kind === 'exam'
        ? BELTS[EXAMS[mode.exam]!.to]!.id
        : mode.kind === 'daily'
          ? mode.date
          : 'kumite';
    track('Game', mode.kind === 'exam' ? 'ExamStarted' : `${mode.kind}Started`, name);
    this.show('playing');
    // Show where to tap in the first exam, and to a newcomer who came by a link
    this.game.play(
      mode,
      (mode.kind === 'exam' && mode.exam === 0) || (!!mode.rival && this.newcomer()),
      coachFor(mode, this.save),
    );
  }

  private onResult(r: MatchResult): void {
    this.lastResult = r;
    document.body.classList.remove('settled');
    this.fillResult(r, true);
    // Let the last hit land visually before the card slides in
    setTimeout(() => this.show('result'), r.passed ? 200 : 500);
  }

  /** Fill the result card; `record` folds the result into the save (once). */
  private fillResult(r: MatchResult, record: boolean): void {
    const s = this.save;
    const title = el('result-title');
    const sub = el('result-sub');
    const swatch = el('result-swatch');
    const next = el('result-next');
    let best = false;
    // A friend's challenge on a locked exam or another day: played, not recorded
    if (record) r.practice = !!r.mode.rival && !this.counts(r.mode);
    const practice = r.practice ?? false;
    swatch.hidden = true;
    el('result-daily').hidden = r.mode.kind !== 'daily';
    el('result-streak').hidden = true;
    el('result-stars').textContent = '';
    const nextStar = el('result-next-star');
    nextStar.hidden = true;
    sub.hidden = false;
    title.classList.remove('jp');

    if (r.mode.kind === 'exam') {
      const examIndex = r.mode.exam;
      const to = BELTS[EXAMS[examIndex]!.to]!;
      const st = r.passed ? starsFor(r.perfects, r.hits) : 0;
      let promoted = r.promoted ?? false;
      if (record && !practice) {
        const outcome = recordExam(s, {
          exam: examIndex,
          passed: r.passed,
          score: r.score,
          stars: st,
          marks: marksFor(examIndex, r),
        });
        promoted = outcome.promoted;
        best = outcome.newBest;
        r.promoted = promoted;
        r.newBest = best;
        r.newMarks = outcome.newMarks;
        if (r.passed) this.game.sfx.promotion();
      } else best = r.newBest ?? false;

      if (practice) {
        title.textContent = r.passed ? t('result.passed') : t('result.yame');
        title.classList.toggle('jp', !r.passed);
        sub.textContent = t('challenge.practice');
        if (r.passed) fillStars(el('result-stars'), st);
        next.textContent = t('result.again');
        this.next = () => this.fight(r.mode);
      } else if (r.passed) {
        title.textContent = promoted ? t('result.promotion') : t('result.passed');
        sub.textContent = promoted
          ? t('result.nowBelt', { belt: beltName(to) })
          : t('result.passedAgain', { belt: beltName(to) });
        swatch.hidden = false;
        paint(swatch, to);
        fillStars(el('result-stars'), st);
        // What the stars count: how many more PERFECT hits this run needed for
        // a star the player doesn't hold yet (a retake goes by the saved best)
        const held = Math.max(st, s.stars[examIndex] ?? 0);
        if (held < 3 && r.hits > 0) {
          const count = perfectsFor(held + 1, r.hits) - r.perfects;
          nextStar.textContent = t('result.nextStar', { count, stars: '★'.repeat(held + 1) });
          nextStar.hidden = false;
        }
        const following = examIndex + 1;
        if (following < EXAMS.length && s.belt >= following) {
          next.textContent = t('result.nextExam', { belt: beltName(BELTS[following + 1]!) });
          this.next = () => this.openIntro({ kind: 'exam', exam: following });
        } else if (following >= EXAMS.length) {
          next.textContent = t('result.kumiteNext');
          this.next = () => this.openIntro({ kind: 'kumite' });
        } else {
          next.textContent = t('result.retake');
          this.next = () => this.fight(r.mode);
        }
      } else {
        title.textContent = t('result.yame');
        title.classList.add('jp');
        sub.textContent = `${t('result.outInRound', { round: r.rounds + 1, total: r.totalRounds })} ${this.failTip(r)}`;
        next.textContent = t('result.retry');
        this.next = () => this.fight(r.mode);
      }
      this.applyBelt();
    } else if (r.mode.kind === 'daily') {
      // A run counts for the day it started on, passed or knocked out
      if (record && !practice && r.mode.date === this.startedOn) {
        r.newBest = recordDaily(s, r.mode.date, r.score);
        r.training = recordTraining(s, r.mode.date);
        if (r.training.counted) this.trackTraining(r.training);
      }
      best = r.newBest ?? false;
      title.textContent = r.passed ? t('result.dailyDone') : t('result.yame');
      title.classList.toggle('jp', !r.passed);
      // "New best!" already says it when this run is the day's best
      sub.textContent = practice
        ? t('challenge.practice')
        : s.daily.best === 0
          ? this.failTip(r)
          : t('result.todaysBest', { score: s.daily.best });
      sub.hidden = !practice && best;
      if (!practice) this.paintResultStreak(r.training);
      this.paintDailyNext(todayISO());
      next.textContent = t('result.again');
      this.next = () => this.fight(r.mode);
    } else {
      if (record && !practice) r.newBest = recordKumite(s, r.score);
      best = r.newBest ?? false;
      title.textContent = t('result.yame');
      title.classList.add('jp');
      sub.textContent = practice
        ? t('challenge.practice')
        : t('result.reachedLevel', { level: r.level + 1 });
      next.textContent = t('result.again');
      this.next = () => this.fight(r.mode);
    }
    // Mastery marks this run earned (known once the exam is recorded): badges
    // landing after the stars, named in the line under them
    const newMarks = r.mode.kind === 'exam' ? (r.newMarks ?? 0) : 0;
    const marksLine = el('result-marks');
    marksLine.hidden = !newMarks;
    if (newMarks) {
      const said = [
        newMarks & MARK.stamp ? t('result.stampEarned') : '',
        newMarks & MARK.stripe ? t('result.stripeEarned', { count: r.bestChain }) : '',
      ].filter(Boolean);
      const row = el('result-stars');
      for (const [bit, cls] of [
        [MARK.stamp, 'seal'],
        [MARK.stripe, 'stripe'],
      ] as const) {
        if (!(newMarks & bit)) continue;
        const badge = document.createElement('span');
        badge.className = `mark-badge ${cls}`;
        badge.setAttribute('aria-hidden', 'true');
        row.append(badge);
      }
      marksLine.replaceChildren(
        ...said.map((text) => {
          const line = document.createElement('span');
          line.textContent = text;
          return line;
        }),
      );
    }
    // A belt earned or a training day recorded: ask once whether to keep
    // progress on this device, after the promotion and stars have landed
    const ask =
      r.mode.kind === 'exam' && r.passed
        ? 'belt'
        : r.mode.kind === 'daily' && r.training
          ? 'streak'
          : null;
    // A run that scored nothing waits: the day stays in memory for the next ask
    if (record && this.consent === null && ask && !practice && r.score > 0) {
      clearTimeout(this.askTimer);
      this.askTimer = window.setTimeout(() => this.askToSave(ask), 1400);
    }
    if (record) {
      this.keep();
      if (r.mode.kind === 'exam') {
        const beltId = BELTS[EXAMS[r.mode.exam]!.to]!.id;
        track('Game', r.passed ? 'ExamPassed' : 'ExamFailed', beltId, r.score);
      } else {
        const name = r.mode.kind === 'daily' ? ruleFor(r.mode.date).id : undefined;
        track('Game', `${r.mode.kind}Ended`, name, r.score);
      }
    }
    // A friend's score: beaten or not, and the way to send one back
    const rival = r.mode.rival;
    const rivalLine = el('result-rival');
    rivalLine.hidden = !rival;
    if (rival) {
      const diff = r.score - rival.score;
      rivalLine.textContent =
        diff > 0
          ? t('result.beatBy', { count: diff })
          : diff < 0
            ? t('result.shortBy', { count: -diff })
            : t('result.tie');
      rivalLine.classList.toggle('won', diff > 0);
      if (record) track('Challenge', diff > 0 ? 'Won' : 'Lost', r.mode.kind, r.score);
    }
    const toBelt = r.mode.kind === 'exam' ? BELTS[EXAMS[r.mode.exam]!.to]! : null;
    this.fillCta(r, toBelt, record);
    const share = el('result-share');
    share.hidden = !(r.passed || r.score > 0);
    // Ready before the tap, so the share sheet opens straight from it
    if (!share.hidden) loadShareCard().catch(() => undefined);
    share.textContent = rival ? t('share.challengeBack') : t('share.btn');

    countUp(el('result-score'), r.score);
    el('result-record').hidden = !best;
    el('result-kills').textContent = fmt(r.kills);
    // Counted, not a percentage: "14/24" reads at any age
    el('result-perfects').textContent = r.hits > 0 ? `${fmt(r.perfects)}/${fmt(r.hits)}` : '–';
    el('result-chain').textContent = fmt(r.bestChain);
    const bestImpact = r.impacts?.length ? Math.max(...r.impacts) : null;
    for (const n of document.querySelectorAll<HTMLElement>('.impact-row'))
      n.hidden = bestImpact === null;
    if (bestImpact !== null) el('result-impact').textContent = fmt(bestImpact);
    document.body.dataset.result = r.passed ? 'pass' : 'fail';
  }

  private failTip(r: MatchResult): string {
    const timing = r.hits > 0 ? r.perfects / r.hits : 0;
    return timing < 0.4 ? t('result.tipTiming') : t('result.tipTelegraph');
  }
}
